import { PropsWithChildren } from 'react';
import { Navigate } from 'react-router-dom';
import { Module } from '@modules/admin/types';
import { usePermission } from '@modules/auth/hooks';

interface PermissionRouteProps extends PropsWithChildren {
  module?: Module;
  requiresView?: boolean;
  requiresAdd?: boolean;
  requiresUpdate?: boolean;
  isPublic?: boolean;
}

/**
 * PermissionRoute component - Checks if user has required permissions to access a route
 * If user doesn't have permission, redirects to access denied page
 */
export default function PermissionRoute({
  children,
  module,
  requiresView = true, // Default to requiring view permission
  requiresAdd = false,
  requiresUpdate = false,
  isPublic = false,
}: Readonly<PermissionRouteProps>) {
  // If route is public or no module specified, allow access
  if (isPublic || !module) {
    return <>{children}</>;
  }

  // Get permissions for the module
  const { canView, canAdd, canUpdate } = usePermission(module);

  // Check if user has required permissions
  const hasRequiredPermissions =
    (!requiresView || canView) && (!requiresAdd || canAdd) && (!requiresUpdate || canUpdate);

  // If user doesn't have required permissions, redirect to access denied
  if (!hasRequiredPermissions) {
    return <Navigate to="/access-denied" replace />;
  }

  return <>{children}</>;
}
